import ContactUs from "@aus/Assets/Icons/ContactUs";
import CrossReference from "@aus/Assets/Icons/CrossReference";
import AusQuickOrder from "@aus/Assets/Icons/quick_order_white";
import AusSupport from "@aus/Assets/Icons/support_white";
import AusSupportWrench from "@aus/Assets/Icons/support_white_wrench";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    title = "title",
    destination = "destination",
    iconSize = "iconSize",
    topIconOffset = "topIconOffset",
    leftIconOffset = "leftIconOffset",
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const link = getLink(state, ownProps.fields.destination);
    return {
        url: link?.url,
        title: link?.title,
    };
};

const mapDispatchToProps = {};

export interface HeaderLinkStyles {
    routerLink?: ClickablePresentationProps;
    iconStyle?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
}

export const headerLinkStyles: HeaderLinkStyles = {
    routerLink: {
        css: css`
            display: inline-block;
            text-align: center;
            padding-bottom: 5px;
            position: relative;
            border: 2px solid transparent;
            top: 5px;
            :hover {
                border-bottom: 2px solid black;
            }
            padding-left: 5px;
            padding-right: 5px;
        `,
    },
    titleTypography: {
        css: css`
            white-space: normal;
            display: block;
            font-weight: 500;
            text-align: center;
            word-break: normal;
            margin-top: 5px;
            max-width: 125px;
        `,
        size: 12,
        ellipsis: true,
        transform: "none",
        color: "text.main",
    },
};

const iconMap = {
    ausQuickOrder: AusQuickOrder,
    ausSupport: AusSupport,
    ausSupportWrench: AusSupportWrench,
    crossReference: CrossReference,
    contactUs: ContactUs,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
        [fields.title]: string;
        [fields.destination]: LinkFieldValue;
        [fields.iconSize]: number;
        [fields.topIconOffset]: number;
        [fields.leftIconOffset]: number;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = headerLinkStyles;
const HeaderLink: FC<Props> = ({ url, fields }) => {
    const showIcon = fields.visibilityState !== "label";
    const showLabel = fields.visibilityState !== "icon";
    const icon = iconMap[(showIcon && fields.icon) || "none"];
    const size = fields.iconSize;

    const iconStyle = {
        color: "text.main",
        css: css`
            display: block;
            margin: auto;
            height: 30px;
            width: 30px;
            position: relative;
            top: -${fields.topIconOffset}px;
            left: -${fields.leftIconOffset}px;
        `,
    };

    const { routerLink } = styles;

    return (
        <Clickable className="headerLink" {...routerLink} href={url} data-test-selector="HeaderLink">
            {showIcon && <Icon {...iconStyle} src={icon} size={size} />}
            {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
            {showLabel && <Typography {...styles.titleTypography}>{`${translate(fields.title)}`}</Typography>}
        </Clickable>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(HeaderLink),
    definition: {
        displayName: "Header Link",
        icon: "Link",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
                isRequired: true,
            },
            {
                name: fields.icon,
                displayName: "Display Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Aus Support",
                        value: "ausSupport",
                    },
                    {
                        displayName: "Aus Support Wrench",
                        value: "ausSupportWrench",
                    },
                    {
                        displayName: "Aus Quick Order",
                        value: "ausQuickOrder",
                    },
                    {
                        displayName: "Cross Reference",
                        value: "crossReference",
                    },
                    {
                        displayName: "Contact Us",
                        value: "contactUs",
                    },
                ],
                defaultValue: "ausQuickOrder",
                fieldType: "General",
                sortOrder: 2,
                isRequired: true,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.destination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
            },
            {
                fieldType: "General",
                name: fields.topIconOffset,
                editorTemplate: "IntegerField",
                defaultValue: 0,
            },
            {
                fieldType: "General",
                name: fields.leftIconOffset,
                editorTemplate: "IntegerField",
                defaultValue: 0,
            },
            {
                fieldType: "General",
                name: fields.iconSize,
                editorTemplate: "IntegerField",
                defaultValue: 30,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
